// diffsona.com/src/components/LoginModal.ts
import { LitElement, html } from 'lit';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { auth } from '../client/FirebaseClient';
import { API_ENDPOINT } from '../constants';
import AppAnalytics from '../client/AnalyticsClient';

class LoginModal extends LitElement {
  createRenderRoot() { return this }

  static properties = {
    open: { type: Boolean, reflect: true }
  };

  open = false;
  private error: string | null = null;
  private loading = false;
  private customMessage: string | null = null;
  private customSource: string | null = null;

  show(message?: string, source?: string) {
    this.open = true;
    this.error = null;
    this.customMessage = message || null;
    this.customSource = source || null;
  }
  hide(click = false) {
    if (click) {
      AppAnalytics.getPlausible()?.trackEvent('manual_close_login_modal');
    }
    this.open = false;
  }

  private async afterAuth(idToken: string) {
    await fetch(`${API_ENDPOINT}/auth`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken })
    });
    AppAnalytics.getPlausible()?.trackEvent('auth_completed');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('auth:refresh'));
    }, 150)
    this.hide();
  }

  async signInWithGoogle() {
    this.error = null;
    this.loading = true;
    try {
      const userCred = await signInWithPopup(auth, new GoogleAuthProvider());
      const idToken = await userCred.user.getIdToken();
      await this.afterAuth(idToken);
    } catch (e: any) {
      this.error = e?.message || 'Google sign-in failed';
    } finally {
      this.loading = false;
    }
  }

  async signInWithEmail(e: Event) {
    e.preventDefault();
    this.error = null; this.loading = true;
    const email = (this.parentElement!.querySelector('#email') as HTMLInputElement).value;
    const pass = (this.parentElement!.querySelector('#password') as HTMLInputElement).value;

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, pass);
      const idToken = await userCred.user.getIdToken();
      await this.afterAuth(idToken);
    } catch {
      try {
        const userCred = await createUserWithEmailAndPassword(auth, email, pass);
        const idToken = await userCred.user.getIdToken();
        await this.afterAuth(idToken);
      } catch (err: any) {
        this.error = err?.message || 'Email sign-in failed';
      }
    } finally {
      this.loading = false;
    }
  }

  render() {
    if (!this.open) return null;

    return html`
      <!-- Backdrop -->
      <div class="fixed inset-0 z-[80] bg-gradient-to-br from-black/60 via-indigo-950/30 to-black/60 backdrop-blur-md
                  animate-[fadeIn_.3s_ease]" @click=${() => this.hide(true)}></div>

      <!-- Modal -->
      <div class="fixed inset-0 z-[90] grid place-items-center p-4">
        <div class="relative w-full max-w-xl rounded-2xl border border-white/20
                    bg-gradient-to-br from-slate-950/95 via-indigo-950/20 to-slate-950/95
                    shadow-2xl shadow-indigo-500/10
                    animate-[fadeIn_.3s_ease,scaleIn_.3s_ease] backdrop-blur-sm">
          <!-- Subtle sparkles -->
          <div class="absolute top-3 right-4 w-1 h-1 bg-indigo-300 rounded-full animate-pulse opacity-60"></div>
          <div class="absolute top-6 left-6 w-1 h-1 bg-purple-300 rounded-full animate-pulse opacity-40" style="animation-delay: 0.5s"></div>

          <style>
            @keyframes fadeIn { from {opacity:0} to {opacity:1} }
            @keyframes scaleIn { from {transform:scale(.95)} to {transform:scale(1)} }
          </style>

          <div class="px-5 py-4 border-b border-white/20 flex items-center justify-between">
            <div></div> <!-- Spacer for centering -->
            <button class="text-slate-400 hover:text-white transition-all duration-300 hover:scale-110 hover:rotate-90
                           w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                    @click=${() => this.hide(true)} aria-label="Close">✕</button>
          </div>

          <div class="p-5 space-y-4">
            <!-- Custom message for rate limits, etc. -->
            ${this.customMessage ? html`
              <div class="relative rounded-xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-orange-500/10
                          px-4 py-3 text-amber-100 text-sm
                          shadow-lg shadow-amber-500/10 overflow-hidden group">
                <!-- Subtle shimmer effect -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/10 to-transparent
                           translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

                <div class="relative z-10 flex items-start gap-2">
                  <span class="text-amber-200 text-lg">⚡</span>
                  <span>${this.customMessage}</span>
                </div>
              </div>
            ` : html`
              <!-- Promo banner -->
              <div class="relative rounded-xl border border-emerald-400/40 bg-gradient-to-r from-emerald-500/15 to-green-500/10
                          px-4 py-3 text-emerald-100 text-sm flex gap-1 flex-wrap items-center
                          shadow-lg shadow-emerald-500/10 overflow-hidden group">
                <!-- Subtle shimmer effect -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/10 to-transparent
                           translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

                <span class="relative z-10">
                  ✨ New here? Get
                </span>
                <strong class="relative z-10 flex gap-1.5 items-center text-emerald-200 group-hover:scale-105 transition-transform duration-300">
                  1200 <img src="/static/images/diffso-coin-64.webp" alt="Credits" class="h-6 w-6 group-hover:animate-pulse" />
                </strong>
                <span class="relative z-10">
                  when you sign up — no card required.
                </span>
              </div>
            `}

            ${this.error ? html`
              <div class="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                ${this.error}
              </div>
            ` : null}

            <form @submit=${(e: Event) => this.signInWithEmail(e)} class="space-y-3">
              <div class="space-y-2">
                <label class="text-xs text-slate-400 font-medium">Email</label>
                <input id="email" type="email"
                  class="w-full rounded-xl bg-slate-900/80 border border-white/20 px-4 py-3
                         focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:border-indigo-400/30
                         transition-all duration-300 hover:border-white/30
                         text-slate-100 placeholder:text-slate-500"
                  placeholder="you@example.com"
                  autocomplete="email" required />
              </div>

              <div class="space-y-2">
                <label class="text-xs text-slate-400 font-medium">Password</label>
                <input id="password" type="password"
                  class="w-full rounded-xl bg-slate-900/80 border border-white/20 px-4 py-3
                         focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:border-indigo-400/30
                         transition-all duration-300 hover:border-white/30
                         text-slate-100 placeholder:text-slate-500"
                  placeholder="••••••••"
                  autocomplete="current-password" required />
              </div>

              <button type="submit"
                class="w-full rounded-xl border border-indigo-400/40 bg-gradient-to-r from-indigo-500/20 to-purple-500/15
                       hover:from-indigo-500/30 hover:to-purple-500/25 hover:border-indigo-400/60
                       px-4 py-3 disabled:opacity-50 cursor-pointer font-bold text-indigo-100 hover:text-white
                       transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98]
                       hover:shadow-lg hover:shadow-indigo-500/20"
                ?disabled=${this.loading}>
                ${this.loading
                  ? html`<span class="flex items-center justify-center gap-2">
                      <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Signing in…
                    </span>`
                  : 'Continue'}
              </button>
            </form>

            <div class="relative py-3">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-white/20"></div>
              </div>
              <div class="relative flex justify-center">
                <span class="px-3 text-xs text-slate-400 bg-gradient-to-r from-slate-950/95 via-indigo-950/20 to-slate-950/95">or</span>
              </div>
            </div>

            <button
              class="w-full rounded-xl border border-white/30 bg-white/10 hover:bg-white/15
                     hover:border-white/40 cursor-pointer px-4 py-3 font-medium disabled:opacity-50
                     flex items-center justify-center gap-3 transition-all duration-300 ease-out
                     hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:shadow-white/10"
              @click=${() => this.signInWithGoogle()}
              ?disabled=${this.loading}
            >
              <svg class="h-5 transition-transform duration-300 group-hover:scale-110" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlns:xlink="http://www.w3.org/1999/xlink" style="display: block;"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></svg>
              ${this.loading
                ? html`<span class="flex items-center gap-2">
                    <div class="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin"></div>
                    Please wait…
                  </span>`
                : 'Continue with Google'}
            </button>

            <p class="text-[11px] text-slate-500 text-center leading-relaxed">
              Free credits are applied automatically on first sign-in. By continuing you agree to our
              <a href="/terms" class="underline hover:text-slate-300 transition-colors duration-200">Terms</a> and
              <a href="/privacy" class="underline hover:text-slate-300 transition-colors duration-200">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('login-modal', LoginModal);

